import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { useVirtualizer, VirtualItem, VirtualizerOptions } from '@tanstack/react-virtual';
import { debounce } from 'lodash';

import { classNames } from '@/utils';

import LoadMoreInfinite from '@/components/base/LoadMoreInfinite/LoadMoreInfinite';

const DEFAULT_ITEM_HEIGHT = 72;

type InfiniteScrollProps<T> = Partial<VirtualizerOptions<HTMLDivElement, HTMLDivElement>> & {
    className?: string;
    data: T[];
    hasMore: boolean;
    inverse?: boolean;
    isLoadingMore?: boolean;
    loadMore?: () => void;
    renderItem: (item: T) => React.ReactNode;
    step?: number;
};

export type InfiniteScrollRef = {
    scrollToBottom: () => void;
    scrollToTop: () => void;
    scrollToIndex: (index: number) => void;
};

const InfiniteScrollComponent = <T,>(
    {
        className,
        data,
        hasMore,
        inverse = false,
        isLoadingMore = false,
        loadMore,
        renderItem,
        step = 20,
        ...rest
    }: InfiniteScrollProps<T>,
    ref: React.Ref<InfiniteScrollRef>
) => {
    const scrollContainer = useRef<HTMLDivElement | null>(null);
    const isLoadingMoreRef = useRef(isLoadingMore);
    const isInitialized = useRef(false);

    const rowVirtualizer = useVirtualizer({
        count: hasMore ? data.length + 1 : data.length,
        getScrollElement: () => scrollContainer.current,
        estimateSize: () => DEFAULT_ITEM_HEIGHT,
        measureElement: (element) =>
            element?.getBoundingClientRect?.()?.height ?? DEFAULT_ITEM_HEIGHT,
        ...rest
    });

    // Scroll view persistence when loading more
    useEffect(() => {
        if (isLoadingMoreRef.current && rowVirtualizer) {
            rowVirtualizer.scrollToIndex(step - 1, { align: 'start', behavior: 'auto' });
        }
        isLoadingMoreRef.current = isLoadingMore;
         
    }, [isLoadingMore]);

    const scrollToBottom = useCallback(() => {
        if (data.length > 0) {
            rowVirtualizer.scrollToIndex(data.length - 1, { align: 'end', behavior: 'smooth' });
        }
    }, [data.length, rowVirtualizer]);

    useImperativeHandle(
        ref,
        () => ({
            scrollToBottom,
            scrollToTop: () => {
                if (data.length > 0) {
                    rowVirtualizer.scrollToIndex(0, { align: 'start', behavior: 'smooth' });
                }
            },
            scrollToIndex: (index: number) => {
                rowVirtualizer.scrollToIndex(index, { align: 'center', behavior: 'smooth' });
            }
        }),
        [rowVirtualizer, data.length, scrollToBottom]
    );

    // Initial scroll to bottom when on inverse mode
    useEffect(() => {
        if (inverse && rowVirtualizer && data.length > 0 && !isInitialized.current) {
            setTimeout(scrollToBottom, 300);
            isInitialized.current = true;
        }
    }, [inverse, rowVirtualizer, data.length, scrollToBottom]);

    const getEndItem = () => {
        const items = rowVirtualizer.getVirtualItems();
        return inverse ? items[0] : items[items.length - 1];
    };

    const isNearTheEnd = () => {
        const endItem = getEndItem();

        if (!endItem) return false;

        return inverse ? endItem.index <= 1 : endItem.index >= data.length - 2;
    };

    const handleLoadMore = debounce(() => {
        if (isNearTheEnd() && !isLoadingMoreRef.current && rowVirtualizer.isScrolling) {
            loadMore?.();
        }
    }, 100);

    // Handle load more when scrolling near the end
    useEffect(() => {
        if (hasMore) {
            handleLoadMore();
        }
         
    }, [hasMore, getEndItem()]);

    const handleItemRender = (index: number) => {
        const item = data[index];
        return item ? renderItem(item) : null;
    };

    return (
        <div ref={scrollContainer} className={classNames('h-full w-full overflow-auto', className)}>
            <div
                className="relative h-full w-full"
                style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => {
                    // Show loader at appropriate end based on inverse mode
                    const showLoader = inverse
                        ? virtualRow.index === 0 && hasMore
                        : virtualRow.index === data.length && hasMore;

                    return (
                        <div
                            key={virtualRow.key}
                            data-index={virtualRow.index}
                            ref={rowVirtualizer.measureElement}
                            className="absolute top-0 w-full"
                            style={{ transform: `translateY(${virtualRow.start}px)` }}>
                            {showLoader ? (
                                <LoadMoreInfinite className="flex justify-center py-3" />
                            ) : (
                                handleItemRender(virtualRow.index)
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const InfiniteScroll = forwardRef(InfiniteScrollComponent) as <T>(
    props: InfiniteScrollProps<T> & { ref?: React.Ref<InfiniteScrollRef> }
) => React.ReactElement;
